import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalIcon, Clock, Trash2, Pencil, CheckCircle2, CircleDot, AlertCircle, MapPin, UserPlus, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const toLocalKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalISOInput = (d) => `${toLocalKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Legacy jobs without a type should look like "confirmed" (safer, keeps current look)
const effectiveType = (j) => j?.type || "confirmed";

const TYPE_STYLES = {
  appointment: {
    label: "Appointment",
    bg: "#fef2f2",
    fg: "#b91c1c",
    border: "#fecaca",
    icon: AlertCircle,
  },
  confirmed: {
    label: "Accepted",
    bg: "#eff6ff",
    fg: "#1d4ed8",
    border: "#bfdbfe",
    icon: CircleDot,
  },
  completed: {
    label: "Completed",
    bg: "#ecfdf5",
    fg: "#047857",
    border: "#a7f3d0",
    icon: CheckCircle2,
  },
};

export default function JobCalendar() {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ customer_id: "", start_at: "", title: "", notes: "", address: "", type: "appointment" });
  const [dragJobId, setDragJobId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [ctx, setCtx] = useState(null); // {x, y, job}
  const ctxRef = useRef(null);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", email: "", phone: "", address: "" });
  const [savingCust, setSavingCust] = useState(false);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  const firstGrid = new Date(monthStart);
  firstGrid.setDate(1 - monthStart.getDay());
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(firstGrid);
    d.setDate(firstGrid.getDate() + i);
    return d;
  });

  const load = async () => {
    const [j, c] = await Promise.all([
      api.get("/jobs", { params: { start: monthStart.toISOString(), end: monthEnd.toISOString() } }),
      api.get("/customers"),
    ]);
    setJobs(j.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  // Close context menu on any click outside
  useEffect(() => {
    const h = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtx(null);
    };
    const esc = (e) => { if (e.key === "Escape") setCtx(null); };
    if (ctx) {
      window.addEventListener("mousedown", h);
      window.addEventListener("keydown", esc);
      return () => { window.removeEventListener("mousedown", h); window.removeEventListener("keydown", esc); };
    }
  }, [ctx]);

  const byDay = useMemo(() => {
    const m = {};
    jobs.forEach((j) => {
      const k = toLocalKey(new Date(j.start_at));
      (m[k] = m[k] || []).push(j);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.start_at.localeCompare(b.start_at)));
    return m;
  }, [jobs]);

  const openCreate = (d) => {
    const start = new Date(d);
    start.setHours(9, 0, 0, 0);
    setEditing(null);
    const first = customers[0];
    setForm({ customer_id: first?.id || "", start_at: toLocalISOInput(start), title: "", notes: "", address: first?.address || "", type: "appointment" });
    setOpen(true);
  };

  const openEdit = (j) => {
    setEditing(j);
    setForm({
      customer_id: j.customer_id,
      start_at: toLocalISOInput(new Date(j.start_at)),
      title: j.title || "",
      notes: j.notes || "",
      address: j.address || "",
      type: effectiveType(j),
    });
    setOpen(true);
  };

  // When customer changes in the create dialog, auto-fill the address from that customer
  const onCustomerChange = (v) => {
    const c = customers.find((x) => x.id === v);
    setForm((f) => ({ ...f, customer_id: v, address: f.address && editing ? f.address : (c?.address || f.address || "") }));
  };

  const save = async () => {
    if (!form.customer_id) return toast.error("Pick a customer");
    if (!form.start_at) return toast.error("Pick a start time");
    try {
      const payload = { ...form, start_at: new Date(form.start_at).toISOString() };
      if (editing) await api.put(`/jobs/${editing.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(editing ? "Job updated" : "Job scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${editing.id}`);
    toast.success("Job removed"); setOpen(false); load();
  };

  const quickSetType = async (job, type) => {
    try {
      await api.patch(`/jobs/${job.id}/type`, { type });
      toast.success(`Marked as ${TYPE_STYLES[type].label}`);
      setCtx(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const quickDelete = async (job) => {
    if (!window.confirm(`Delete ${job.customer_name}'s job?`)) return;
    await api.delete(`/jobs/${job.id}`);
    toast.success("Job removed"); setCtx(null); load();
  };

  const todayKey = toLocalKey(new Date());
  const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const onDropOnDay = async (e, day) => {
    e.preventDefault();
    setDragOverKey(null);
    const jid = e.dataTransfer.getData("text/jobid") || dragJobId;
    setDragJobId(null);
    if (!jid) return;
    const job = jobs.find((j) => j.id === jid);
    if (!job) return;
    const original = new Date(job.start_at);
    if (toLocalKey(original) === toLocalKey(day)) return;
    const next_ = new Date(day);
    next_.setHours(original.getHours(), original.getMinutes(), 0, 0);
    try {
      await api.put(`/jobs/${jid}`, {
        customer_id: job.customer_id,
        start_at: next_.toISOString(),
        title: job.title || "",
        notes: job.notes || "",
        address: job.address || "",
        type: effectiveType(job),
      });
      toast.success(`Moved ${job.customer_name} to ${MONTHS[next_.getMonth()]} ${next_.getDate()}`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const openCtx = (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    const menuW = 220;
    const menuH = 220;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setCtx({ x, y, job });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="job-calendar">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--qq-accent-50)", color: "var(--qq-accent)", border: "1px solid var(--qq-accent-100)" }}>
            <CalIcon className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Schedule</div>
            <div className="font-display font-extrabold text-xl text-slate-900" data-testid="cal-title">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="cal-prev" onClick={prev} className="p-2 rounded-md hover:bg-slate-100 border border-slate-200"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date(now.getFullYear(), now.getMonth(), 1))} className="px-3 py-1.5 rounded-md hover:bg-slate-100 border border-slate-200 text-xs font-bold uppercase tracking-wide">Today</button>
          <button data-testid="cal-next" onClick={next} className="p-2 rounded-md hover:bg-slate-100 border border-slate-200"><ChevronRight className="w-4 h-4" /></button>
          <Button data-testid="cal-add" onClick={() => openCreate(new Date())} className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> Add Job</Button>
        </div>
      </div>

      {/* Legend */}
      <div className="px-5 py-2 border-b border-slate-200 flex flex-wrap items-center gap-3 bg-slate-50/60" data-testid="cal-legend">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Legend</span>
        {Object.entries(TYPE_STYLES).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: s.fg }}>
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.bg, border: `1px solid ${s.border}` }} />
            {s.label}
          </span>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">Tip: right-click a job to change status</span>
      </div>

      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {DAYS.map((d) => <div key={d} className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">{d}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const key = toLocalKey(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const dayJobs = byDay[key] || [];
          // Dynamic height compression — tighter padding when many items, scrollable past 6
          const dense = dayJobs.length >= 4;
          return (
            <div
              key={i}
              onClick={() => openCreate(d)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDragEnter={() => setDragOverKey(key)}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverKey((k) => (k === key ? null : k)); }}
              onDrop={(e) => onDropOnDay(e, d)}
              className={`min-h-[140px] sm:min-h-[160px] border-r border-b border-slate-100 p-2 cursor-pointer hover:bg-slate-50 transition-colors flex flex-col ${inMonth ? "bg-white" : "bg-slate-50/60"} ${dragOverKey === key ? "ring-2 ring-inset" : ""}`}
              style={dragOverKey === key ? { boxShadow: "inset 0 0 0 2px var(--qq-accent)" } : undefined}
              data-testid={`cal-day-${key}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-xs font-bold ${isToday ? "w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center" : inMonth ? "text-slate-900" : "text-slate-400"}`}>
                  {d.getDate()}
                </div>
                {dayJobs.length > 0 && <span className="text-[10px] font-bold text-slate-400" data-testid={`cal-day-count-${key}`}>{dayJobs.length}</span>}
              </div>
              <div
                className={`mt-1.5 flex-1 overflow-y-auto pr-0.5 ${dense ? "space-y-0.5" : "space-y-1"}`}
                style={{ maxHeight: "160px" }}
                onWheel={(e) => e.stopPropagation()}
              >
                {dayJobs.map((j) => {
                  const t = effectiveType(j);
                  const s = TYPE_STYLES[t] || TYPE_STYLES.confirmed;
                  const Icon = s.icon;
                  return (
                    <button
                      key={j.id}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("text/jobid", j.id); e.dataTransfer.effectAllowed = "move"; setDragJobId(j.id); }}
                      onDragEnd={() => { setDragJobId(null); setDragOverKey(null); }}
                      onClick={(e) => { e.stopPropagation(); if (dragJobId) return; openEdit(j); }}
                      onContextMenu={(e) => openCtx(e, j)}
                      data-testid={`cal-job-${j.id}`}
                      data-type={t}
                      className={`w-full text-left text-[11px] font-semibold leading-tight ${dense ? "px-1 py-0.5" : "px-1.5 py-1"} rounded cursor-grab active:cursor-grabbing ${dragJobId === j.id ? "opacity-40" : ""} ${t === "completed" ? "line-through opacity-80" : ""}`}
                      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
                      title={`${s.label} · ${fmtTime(j.start_at)} · ${j.customer_name}${j.title ? ` · ${j.title}` : ""}${j.address ? `\n${j.address}` : ""}  (right-click for options)`}
                    >
                      {/* Row 1: icon + time + (APPOINTMENT badge if red) */}
                      <div className="flex items-center gap-1 truncate">
                        <Icon className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate font-bold">{fmtTime(j.start_at)}</span>
                        {t === "appointment" && !dense && (
                          <span className="ml-auto text-[8px] font-extrabold uppercase tracking-wider px-1 py-[1px] rounded shrink-0" style={{ background: s.fg, color: s.bg }}>
                            Appt
                          </span>
                        )}
                      </div>
                      {/* Row 2: customer name */}
                      <div className="truncate text-[10px] font-semibold opacity-90 pl-3.5">{j.customer_name}</div>
                      {/* Row 3: title under the time (for confirmed jobs mainly, but shown for all) */}
                      {j.title && t !== "completed" && (
                        <div className="truncate text-[10px] font-bold pl-3.5">{j.title}</div>
                      )}
                      {/* Row 4: address */}
                      {j.address && t !== "completed" && (
                        <div className="flex items-center gap-1 text-[10px] font-medium opacity-80 pl-3.5 truncate">
                          <MapPin className="w-2.5 h-2.5 shrink-0" />
                          <span className="truncate">{j.address}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Context Menu */}
      {ctx && (
        <div
          ref={ctxRef}
          className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 w-56"
          style={{ left: ctx.x, top: ctx.y }}
          data-testid="cal-ctx-menu"
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Job</div>
            <div className="text-xs font-bold text-slate-900 truncate">{ctx.job.customer_name}</div>
            <div className="text-[11px] text-slate-500">{fmtTime(ctx.job.start_at)}{ctx.job.title ? ` · ${ctx.job.title}` : ""}</div>
            {ctx.job.address && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{ctx.job.address}</span>
              </div>
            )}
          </div>
          {Object.entries(TYPE_STYLES).map(([k, s]) => {
            const Icon = s.icon;
            const active = effectiveType(ctx.job) === k;
            return (
              <button
                key={k}
                onClick={() => quickSetType(ctx.job, k)}
                data-testid={`ctx-mark-${k}`}
                className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center gap-2 hover:bg-slate-50 ${active ? "bg-slate-50" : ""}`}
                style={{ color: s.fg }}
              >
                <Icon className="w-3.5 h-3.5" />
                Mark as {s.label}
                {active && <span className="ml-auto text-[10px] text-slate-400">current</span>}
              </button>
            );
          })}
          <div className="border-t border-slate-100 my-1" />
          <button
            onClick={() => { setCtx(null); openEdit(ctx.job); }}
            data-testid="ctx-edit"
            className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-2 hover:bg-slate-50"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit details
          </button>
          <button
            onClick={() => quickDelete(ctx.job)}
            data-testid="ctx-delete"
            className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 flex items-center gap-2 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete job
          </button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="job-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit scheduled job" : "Schedule a job"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="job-type"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="appointment" data-testid="job-type-appointment">
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_STYLES.appointment.bg, border: `1px solid ${TYPE_STYLES.appointment.border}` }} /> Appointment</span>
                  </SelectItem>
                  <SelectItem value="confirmed" data-testid="job-type-confirmed">
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_STYLES.confirmed.bg, border: `1px solid ${TYPE_STYLES.confirmed.border}` }} /> Accepted</span>
                  </SelectItem>
                  <SelectItem value="completed" data-testid="job-type-completed">
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_STYLES.completed.bg, border: `1px solid ${TYPE_STYLES.completed.border}` }} /> Completed</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer</Label>
              <div className="flex gap-2 items-stretch">
                <div className="flex-1 min-w-0">
                  <Select value={form.customer_id} onValueChange={onCustomerChange}>
                    <SelectTrigger data-testid="job-customer"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {customers.length === 0 && <div className="text-xs text-slate-500 p-2">No customers yet. Click "New" to add one.</div>}
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="job-new-customer-btn"
                  onClick={() => { setNewCust({ name: "", email: "", phone: "", address: "" }); setNewCustOpen(true); }}
                  className="shrink-0 whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> New
                </Button>
              </div>
              {(() => {
                const sel = customers.find((c) => c.id === form.customer_id);
                if (!sel) return null;
                const hasInfo = sel.email || sel.phone;
                return (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="job-customer-info">
                    {sel.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {sel.email}</span>}
                    {sel.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {sel.phone}</span>}
                    {!hasInfo && <span className="italic text-slate-400">No contact info on file for this customer.</span>}
                  </div>
                );
              })()}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start date &amp; time</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="job-start" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Job title (optional)</Label>
              <Input placeholder="e.g. Driveway pour" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
              <Input placeholder="e.g. 123 Main St, Springfield" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="job-address" />
              <p className="text-[10px] text-slate-500 mt-1">Shows on the calendar for appointments &amp; accepted jobs.</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="job-notes" />
            </div>
          </div>
          <DialogFooter className="justify-between">
            {editing ? (
              <Button variant="outline" onClick={remove} data-testid="job-delete" className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="job-save">
                {editing ? <><Pencil className="w-4 h-4 mr-1.5" /> Save</> : <><Plus className="w-4 h-4 mr-1.5" /> Schedule</>}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="max-w-md" data-testid="job-new-customer-dialog">
          <DialogHeader><DialogTitle>Add a new customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input autoFocus value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="job-newcust-name" placeholder="e.g. John Smith" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="job-newcust-email" placeholder="customer@example.com" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
              <Input type="tel" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="job-newcust-phone" placeholder="(555) 123-4567" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
              <Textarea rows={2} value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} data-testid="job-newcust-address" placeholder="123 Main St, Springfield, IL 62701" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustOpen(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={savingCust || !newCust.name.trim()}
              data-testid="job-newcust-save"
              onClick={async () => {
                if (!newCust.name.trim()) { toast.error("Name is required"); return; }
                setSavingCust(true);
                try {
                  const payload = {
                    name: newCust.name.trim(),
                    email: newCust.email.trim(),
                    phone: newCust.phone.trim(),
                    address: newCust.address.trim(),
                  };
                  const { data } = await api.post("/customers", payload);
                  setCustomers((xs) => [...xs, data].sort((a, b) => a.name.localeCompare(b.name)));
                  // Auto-select the new customer and prefill the job address with theirs
                  setForm((f) => ({ ...f, customer_id: data.id, address: data.address || f.address }));
                  setNewCustOpen(false);
                  toast.success(`Added ${data.name}`);
                } catch (e) {
                  toast.error(formatError(e));
                } finally {
                  setSavingCust(false);
                }
              }}
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> {savingCust ? "Adding…" : "Add customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
